import { Text } from "@mantine/core";
import { isEmpty } from "lodash";
import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import Spoiler from "../../../../components/Spoiler/Spoiler.tsx";
import { TableData, TableRow } from "../../../../components/Table/types.ts";
import TableFilesDisplay from "../../../../components/TableFilesDisplay/TableFilesDisplay.tsx";
import { CollectionActivity } from "../../../../types/collectionActivity.ts";
import { formatLogDetails } from "../../../../utils/collectionActivity.ts";
import { toLocalDate } from "../../../../utils/date.ts";
import { toLocalNumber } from "../../../../utils/number.ts";
import { toHumanReadable } from "../../../../utils/string.ts";
import { getUserDisplayName } from "../../../../utils/user.ts";
import { useStyles } from "../collectionActivityTab.styles.ts";

interface Props {
  data: CollectionActivity[] | null | undefined;
}

export function useCollectionActivityTableData({ data }: Props): TableData {
  const { classes } = useStyles();

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "invoiceNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "invoiceNumber",
          }}
        >
          Inv No
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "createdAt",
          }}
        >
          Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "150px",
    },
    {
      accessor: "dpd",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "Invoice", fieldName: "dpd" }}
        >
          DPD
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "100px",
    },
    {
      accessor: "collectorAssigned",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "collectorAssigned",
          }}
        >
          Collector Assigned
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "category",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "category",
          }}
        >
          Collection Activity Category
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "detailCollectionActivity",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "detailCollectionActivity",
          }}
        >
          Detail Collection Activity
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "resultCategory",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "resultCategory",
          }}
        >
          Result Category
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "resultDescription",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "resultDescription",
          }}
        >
          Result Description
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "paidAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "Invoice", fieldName: "totalPaid" }}
        >
          Paid Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "paymentStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Invoice",
            fieldName: "paymentStatus",
          }}
        >
          Payment Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "200px",
    },
    {
      accessor: "attachment",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "attachment",
          }}
        >
          Attachment
        </DatabaseMetadataPopover>
      ),
      width: "135px",
    },
    {
      accessor: "logDetails",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CollectionActivity",
            fieldName: "logDetails",
          }}
        >
          Log Details
        </DatabaseMetadataPopover>
      ),
      width: "300px",
    },
  ];

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        invoiceNumber: item?.invoice?.invoiceNumber,
        createdAt: item.createdAt && toLocalDate(item.createdAt),
        dpd: toLocalNumber(item.invoice.dpd),
        collectorAssigned: item.collectorAssigned && (
          <Text className={classes.collectorAssignedWrapper}>
            {getUserDisplayName(item.collectorAssigned)}
          </Text>
        ),
        category: item.category && toHumanReadable(item.category),
        detailCollectionActivity: item.detailCollectionActivity,
        resultCategory:
          item.resultCategory && toHumanReadable(item.resultCategory),
        resultDescription: item.resultDescription,
        paidAmount:
          item?.invoice?.totalPaid && toLocalNumber(item?.invoice?.totalPaid),
        paymentStatus:
          item?.invoice?.paymentStatus &&
          toHumanReadable(item?.invoice?.paymentStatus),
        attachment:
          item.attachment && !isEmpty(item.attachment) ? (
            <TableFilesDisplay files={[item.attachment]} />
          ) : null,
        logDetails: !isEmpty(item?.logDetails) ? (
          <Spoiler text={formatLogDetails(item.logDetails)} />
        ) : null,
      };
    });
  }, [data, classes]);

  return [columns, rows];
}
